import { extends as _extends, objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useState, useRef, useMemo, useCallback } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useSwipeable } from 'react-swipeable';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';
import { GripperBox, Backdrop, Box } from './bottom-sheet.styles.js';
import { useDictionary } from '../intl/hooks.js';

var _excluded = ["children", "gripped", "onClose", "onOpen", "onStartClose", "onStartOpen", "onFinishClose", "opened", "rootSelector", "track", "position", "staticHeight", "zIndex"];
var Gripper = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React__default.createElement(GripperBox, _extends({
    type: "button"
  }, props, {
    ref: ref
  }));
});
var MAX_OFFSET = 95;
var MIN_OFFSET = 0;
var DEFAULT_OFFSET = 100;
var EASE_IN_OUT_TRANSITION = '400ms ease-in-out';
var LINEAR_TRANSITION = '100ms';
var BottomSheet = function BottomSheet(props) {
  var children = props.children,
      _props$gripped = props.gripped,
      gripped = _props$gripped === void 0 ? false : _props$gripped,
      onClose = props.onClose,
      onOpen = props.onOpen,
      onStartClose = props.onStartClose,
      onStartOpen = props.onStartOpen,
      onFinishClose = props.onFinishClose,
      opened = props.opened,
      _props$rootSelector = props.rootSelector,
      rootSelector = _props$rootSelector === void 0 ? '#root' : _props$rootSelector,
      _props$track = props.track,
      track = _props$track === void 0 ? 'touch' : _props$track,
      _props$position = props.position,
      position = _props$position === void 0 ? 'fixed' : _props$position,
      _props$staticHeight = props.staticHeight,
      staticHeight = _props$staticHeight === void 0 ? false : _props$staticHeight,
      _props$zIndex = props.zIndex,
      zIndex = _props$zIndex === void 0 ? 1 : _props$zIndex,
      attrs = _objectWithoutPropertiesLoose(props, _excluded);

  var _useState = useState(DEFAULT_OFFSET + "%"),
      offsetY = _useState[0],
      setOffsetY = _useState[1];

  var _useState2 = useState(DEFAULT_OFFSET),
      initialOffsetY = _useState2[0],
      setInitialOffsetY = _useState2[1];

  var _useState3 = useState(EASE_IN_OUT_TRANSITION),
      transition = _useState3[0],
      setTransition = _useState3[1];

  var _useState4 = useState(null),
      root = _useState4[0],
      setRoot = _useState4[1];

  var t = useDictionary({
    componentName: 'BottomSheet'
  });
  var boxRef = useRef(null);
  var backdropRef = useRef(null);

  var getDefaultOffset = function getDefaultOffset() {
    var _boxRef$current, _backdropRef$current;

    if (!gripped) {
      return MIN_OFFSET;
    }

    var boxHeight = (_boxRef$current = boxRef.current) == null ? void 0 : _boxRef$current.clientHeight;
    var viewportHeight = (_backdropRef$current = backdropRef.current) == null ? void 0 : _backdropRef$current.clientHeight;
    var visiblePart = 0;

    if (viewportHeight !== undefined && boxHeight !== undefined) {
      visiblePart = 100 - 30 * viewportHeight / boxHeight;
    }

    return visiblePart;
  };

  var trackType = useMemo(function () {
    return track === 'touch' ? {
      trackTouch: true
    } : {
      trackMouse: true
    };
  }, [track]);
  var swipers = useSwipeable(_extends({
    onSwiping: function onSwiping(eventData) {
      var _boxRef$current2;

      var dir = eventData.dir,
          deltaY = eventData.deltaY;
      var boxHeight = (_boxRef$current2 = boxRef.current) == null ? void 0 : _boxRef$current2.clientHeight;

      if (boxHeight) {
        var delta = deltaY / boxHeight * 100;

        if ((dir === 'Up' || dir === 'Down') && initialOffsetY + delta < MAX_OFFSET && initialOffsetY + delta > MIN_OFFSET) {
          setOffsetY(initialOffsetY + delta + "%");
          setTransition(LINEAR_TRANSITION);
        }
      }
    },
    onSwiped: function onSwiped(eventData) {
      var _boxRef$current3, _backdropRef$current2;

      var dir = eventData.dir,
          absY = eventData.absY;
      var nextOffsetY = parseInt(offsetY, 10);
      var boxHeight = (_boxRef$current3 = boxRef.current) == null ? void 0 : _boxRef$current3.clientHeight;
      var viewportHeight = (_backdropRef$current2 = backdropRef.current) == null ? void 0 : _backdropRef$current2.clientHeight;

      if (boxHeight && viewportHeight) {
        if (absY / viewportHeight > 0.05) {
          if (dir === 'Up') {
            nextOffsetY = MIN_OFFSET;
          }

          if (dir === 'Down') {
            nextOffsetY = getDefaultOffset();
          }
        } else {
          nextOffsetY = getDefaultOffset();
        }
      }

      setTransition(EASE_IN_OUT_TRANSITION);
      setInitialOffsetY(nextOffsetY);
      setOffsetY(nextOffsetY + "%");
    },
    onTap: function onTap() {
      var nextOffsetY = initialOffsetY === MIN_OFFSET ? getDefaultOffset() : MIN_OFFSET;
      setInitialOffsetY(nextOffsetY);
      setOffsetY(nextOffsetY + "%");
    }
  }, trackType));
  var handlers = useMemo(function () {
    return gripped ? swipers : null;
  }, [gripped, swipers]);
  var handleBackdropClick = useCallback(function (e) {
    if (e.target !== e.currentTarget) {
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    } else if (initialOffsetY === MIN_OFFSET) {
      var nextOffsetY = getDefaultOffset();
      setInitialOffsetY(nextOffsetY);
      setOffsetY(nextOffsetY + "%");
    }
  }, [onClose, initialOffsetY]);
  var backdropOpacity = useMemo(function () {
    var defaultOffset = getDefaultOffset();
    var currentOffset = parseInt(offsetY, 10);
    return Math.floor((defaultOffset - currentOffset) / defaultOffset * 0.5 * 100) / 100;
  }, [offsetY, getDefaultOffset]);
  var handleEntering = useCallback(function () {
    var visiblePart = getDefaultOffset();
    setOffsetY(visiblePart + "%");
    setInitialOffsetY(visiblePart);
  }, [getDefaultOffset, onStartOpen]);
  var handleExiting = useCallback(function () {
    setOffsetY(DEFAULT_OFFSET + "%");
    setInitialOffsetY(DEFAULT_OFFSET);
  }, []);
  var handleExit = useCallback(function () {
    if (typeof onStartClose === 'function') {
      onStartClose();
    }
  }, [onStartClose]);
  var handleExited = useCallback(function () {
    if (typeof onClose === 'function') {
      onClose();
    }

    if (typeof onFinishClose === 'function') {
      onFinishClose();
    }
  }, [onClose, onFinishClose, opened]);
  var handleEntered = useCallback(function () {
    if (typeof onOpen === 'function') {
      onOpen();
    }
  }, [onOpen, opened]);
  var handleEnter = useCallback(function () {
    if (typeof onStartOpen === 'function') {
      onStartOpen();
    }

    setRoot(document.querySelector(rootSelector));
  }, [rootSelector]);
  var content = useMemo(function () {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Backdrop, {
      ref: backdropRef,
      gripped: gripped,
      opacity: Number.isNaN(backdropOpacity) ? 0 : backdropOpacity,
      position: position,
      onPointerUp: handleBackdropClick,
      zIndex: zIndex
    }), /*#__PURE__*/React__default.createElement(RemoveScroll, null, /*#__PURE__*/React__default.createElement(Box, _extends({}, attrs, {
      staticHeight: staticHeight,
      transition: transition,
      role: "dialog",
      "aria-modal": "true",
      ref: boxRef,
      gripped: gripped,
      position: position,
      zIndex: zIndex,
      offsetY: offsetY
    }), gripped && /*#__PURE__*/React__default.createElement(Gripper, _extends({}, handlers, {
      "aria-label": initialOffsetY === MIN_OFFSET ? t('minimize') : t('maximize')
    })), children)));
  }, [gripped, backdropOpacity, handleBackdropClick, transition, offsetY, handlers, initialOffsetY, attrs, children]);
  var portal = useMemo(function () {
    return root ? /*#__PURE__*/createPortal(content, root) : null;
  }, [content, root]);
  return /*#__PURE__*/React__default.createElement(Transition, {
    in: opened,
    timeout: 400,
    unmountOnExit: true,
    appear: true,
    onEnter: handleEnter,
    onExit: handleExit,
    onEntering: handleEntering,
    onExiting: handleExiting,
    onExited: handleExited,
    onEntered: handleEntered
  }, function (state) {
    return state !== 'exited' ? portal : null;
  });
};

export { BottomSheet, Gripper };
